import { SelectionModel } from '@angular/cdk/collections';
import {
  Component,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import {
  Subject,
  debounceTime,
  distinctUntilChanged,
  filter,
  of,
  take,
} from 'rxjs';
import { UserService } from '../crud.service';
import { ActionSystem } from 'src/app/domain/ActionSystem';
import { CustomerOriginEnum } from 'src/app/domain/enums/customer-origin.enum';
import { IUser } from 'src/app/domain/registration/models/IUser';
import { GlobalUtils } from 'src/app/shared/global-utils';
import { MatDialog } from '@angular/material/dialog';
import { DialogConfirmComponent } from 'src/app/shared/components/dialog-confirm/dialog-confirm.component';
import { ResetPasswordDialogComponent } from './reset-password-dialog/reset-password-dialog.component';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { AuthService } from 'src/app/views/auth/shared/auth.service';
import { IUserGroup } from 'src/app/domain/registration/models/IUserGroup';
import { S3_URL_BASE } from 'src/environments/environment';
import { COLUMNS_TO_SHOW_WEB, TABLE_CONFIG_COLUMNS } from '../crud-config';
import { MediaObserver } from '@angular/flex-layout';
import { TypeIndexEnum } from 'src/app/domain/registration/enums/type-index.enum';
import { ITableResponsive } from 'src/app/domain/ITableResponsive';
import { IColumnConfigTableResponsive } from 'src/app/domain/IColumnConfigTableResponsive';
import moment from 'moment';

@Component({
  selector: 'app-user-index',
  templateUrl: './crud-index.component.html',
  styleUrls: ['./crud-index.component.sass'],
})
export class UserCrudIndex implements ITableResponsive {
  filterForm: FormGroup;
  action: ActionSystem;
  user: IUser | null;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  displayedColumns = COLUMNS_TO_SHOW_WEB;
  length: 0;
  params = { getRelation: 1, limit: 10, page: 0, filter: '' };
  unsub$ = new Subject<never>();
  selection = new SelectionModel<any>(true, []);
  configColumns: IColumnConfigTableResponsive[] = TABLE_CONFIG_COLUMNS;
  dataSource: MatTableDataSource<any> = new MatTableDataSource<any>([]);
  dataSelect: { type: TypeIndexEnum; select: boolean } = {
    type: TypeIndexEnum.CRUD,
    select: false,
  };

  constructor(
    public dialog: MatDialog,
    public service: UserService,
    public toastService: ToastService,
    public mediaObserver: MediaObserver,
    private authService: AuthService,
    private fb: FormBuilder
  ) {
      this.user = this.authService.getUser();
  }

  ngOnInit(): void {
    this.initForm();
    this.loadList();
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  initForm() {
    this.filterForm = this.fb.group({
      city: ['Todos', []],
      state: ['Todos', []],
      createdAt: [null, []],
    });
  }

  /**
   * Retorna o label do formulário de acordo com selecionado
   * @returns
   */
  public getCustomerOrigin(value: number) {
    return value === CustomerOriginEnum.LOJA ? 'Loja' : 'Online';
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }

    this.selection.select(...this.dataSource.data);
  }

  loadList() {
    let params = this.params;

    this.service.listAll(params).subscribe((dataWithPagination: any) => {
      let data = dataWithPagination[0];
      this.length = dataWithPagination[1];

      this.dataSource = new MatTableDataSource<any>(
        data.map((item: any) => {
          item['createdAt'] = moment(item.createdAt).format('DD/MM/YYYY');
          return item;
        })
      );
    });
  }

  pageEvent(event: PageEvent): PageEvent {
    this.changePage({ page: event.pageIndex, limit: event.pageSize });
    return event;
  }

  compareValue(value1: { id: number }, value2: { id: number }): boolean {
    if (!value1 || !value2) {
      return false;
    }
    return value1.id === value2.id;
  }

  getUserGroups(userGroups: IUserGroup[]): string {
    return userGroups.map((group: IUserGroup) => group.name).join(' | ');
  }

  getImageUrl(avatarUrl: string): string {
    return `${S3_URL_BASE}${avatarUrl}`;
  }

  changePage(event: any) {
    this.params.page = event.page;
    this.params.limit = event.limit;
    this.loadList();
  }

  filter(event: any) {
    if (!event.target) return;
    of(event.target.value)
      .pipe(
        filter((query: string) => query.length > 2 || query == ''),
        debounceTime(500),
        distinctUntilChanged()
      )
      .subscribe({
        next: (query: string) => {
          this.params.filter = query;
          this.loadList();
        },
      });
  }

  public syncMerchantUserGroups() {
    this.service.syncMerchantUserGroups().pipe(take(1)).subscribe({
      next: () => {
        this.toastService.showSuccess('Grupos sincronizados com sucesso!');
      },
      error: (e) => {
        this.toastService.showError('Erro ao sincronizar grupos!');
        console.log(e);
      },
    });
  }

  public resetUserPassword(user: IUser) {
    this.dialog
      .open(ResetPasswordDialogComponent, {
        data: { user },
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((result) => {
        if (!result) return;

        this.service
          .adminResetPassword(user.id, result)
          .pipe(take(1))
          .subscribe({
            next: (response: any) => {
              const generatedPassword = response?.password;
              this.toastService.showSuccess(
                generatedPassword
                  ? `Senha redefinida com sucesso! Senha: ${generatedPassword}`
                  : 'Senha redefinida com sucesso!'
              );
            },
            error: (e) => {
              this.toastService.showError(
                e?.error?.message || 'Erro ao redefinir a senha!'
              );
              console.log(e);
            },
          });
      });
  }

  public deleteUser(user: IUser) {
    GlobalUtils.openDialog({
      dialog: this.dialog,
      data: { title: `Remover o usuário: ${user.firstName}` },
      component: DialogConfirmComponent,
      callBack: (data: any) => {
        if (data?.result) {
          if (user.id == this.user?.id) {
            this.toastService.showError('Você não pode remover seu usuário!');
          } else {
            this.service
              .delete(user.id)
              .pipe(take(1))
              .subscribe({
                next: () => {
                  this.toastService.showSuccess(
                    'Usuário removido com sucesso!'
                  );
                  this.loadList();
                },
                error: (e) => console.log(e),
              });
          }
        }
      },
    });
  }

  ngOnDestroy(): void {}
}
